import { useState } from "react";
import Bugsnag from "@bugsnag/js";
import { Loader2, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { normalizeDistrictDomain } from "@/lib/districtDomain";
import { createDistrict } from "@/services/systemAdminService";

interface AddDistrictDialogProps {
  onCreated: () => Promise<void>;
}

export const AddDistrictDialog = ({ onCreated }: AddDistrictDialogProps) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [domainError, setDomainError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const openDialog = () => {
    setName("");
    setDomain("");
    setDomainError(null);
    setOpen(true);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;

    const { domain: normalized, error: validationError } = normalizeDistrictDomain(domain);
    if (!normalized) {
      setDomainError(validationError);
      return;
    }

    setDomainError(null);
    setBusy(true);
    try {
      await createDistrict(name, normalized);
      toast({ title: "District created" });
      setOpen(false);
      await onCreated();
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      const description = error.message;
      const isDuplicateDomain = /duplicate.*domain|district.*domain.*exists/i.test(description);
      const errorMessage = isDuplicateDomain
        ? "That domain is already assigned to another district."
        : description;
      if (!isDuplicateDomain) {
        Bugsnag.notify(error, (event) => {
          event.severity = "warning";
        });
      }
      setDomainError(errorMessage);
      toast({
        title: "Could not create district",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button type="button" onClick={openDialog}>
        <Plus className="h-4 w-4" aria-hidden />
        Add district
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add district</DialogTitle>
            <DialogDescription>Create a new district and its email domain.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <label htmlFor="add-district-name" className="text-sm text-gray-600">
                New district
              </label>
              <Input
                id="add-district-name"
                className="min-w-0 w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="District name"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <label htmlFor="add-district-domain" className="text-sm text-gray-600">
                District email domain
              </label>
              <Input
                id="add-district-domain"
                className="min-w-0 w-full"
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value);
                  setDomainError(null);
                }}
                placeholder="District email domain"
                aria-describedby={domainError ? "add-district-domain-error" : undefined}
                aria-invalid={Boolean(domainError)}
              />
              {domainError ? (
                <p id="add-district-domain-error" role="alert" className="text-sm text-red-600">
                  {domainError}
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={busy || !name.trim()}
              onClick={() => void handleCreate()}
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Adding…
                </>
              ) : (
                "Add district"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
